import React from "react";
import { useTranslation } from "react-i18next";
import NoAuth from "../no-auth";
import { useDarkMode } from "@hooks/dark-mode";

import BlurText from "../../../assets/styles/BlurText";

// Icons for values section
import { FaLeaf, FaEye, FaLightbulb, FaLock, FaRocket, FaLinkedin, FaBookOpen } from "react-icons/fa";
import { motion } from "framer-motion";

// Add Team image imports
import { Jarl, Oliver, Tomas, Daniel, Marcus, Nikolaj } from "@assets";

import CallToAction from "../home/CallToAction";

// Add TeamCard component
function TeamCard({ name, role, avatar, linkedin }: { name: string; role: string; avatar: string; linkedin: string }) {
  const { dark } = useDarkMode();

  return (
    <div className="group bg-secBg rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
      <div className="w-full h-80 overflow-hidden flex-none">
        <img
          src={avatar}
          alt={name}
          className="w-full h-full object-cover transform scale-105"
        />
      </div>
      <div className="px-4 py-5 flex justify-between items-center flex-none">
        <div className="flex flex-col">
          <div className="font-semibold text-2xl">{name}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{role}</div>
        </div>
        <a href={linkedin} target="_blank" rel="noopener noreferrer" className={dark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"}>
          <FaLinkedin className="text-2xl" />
        </a>
      </div>
    </div>
  );
}

const AboutUsContent = () => {
  const { t } = useTranslation();
  const { dark } = useDarkMode();
  const accent = dark ? '#80ABF9' : '#1673FF';

  const [showRest, setShowRest] = React.useState(false);
  React.useEffect(() => {
    const id = setTimeout(() => setShowRest(true), 650);
    return () => clearTimeout(id);
  }, []);

  const valueIcons = {
    sustainability: FaLeaf,
    transparency: FaEye,
    innovation: FaLightbulb,
    privacy: FaLock
  };

  const values = [
    { key: 'sustainability', icon: valueIcons.sustainability },
    { key: 'transparency', icon: valueIcons.transparency },
    { key: 'innovation', icon: valueIcons.innovation },
    { key: 'privacy', icon: valueIcons.privacy }
  ];

  // Add team array
  const team = [
    { name: "Marcus", roleKey: "ceo", avatar: Marcus, linkedin: "https://www.linkedin.com/in/marcus-wanned/" },
    { name: "Nikolaj", roleKey: "founder", avatar: Nikolaj, linkedin: "https://www.linkedin.com/in/nikolaj-mackenzie/" },
    { name: "Tomas", roleKey: "cto", avatar: Tomas, linkedin: "https://www.linkedin.com/in/tomasorosenvindrieck/" },
    { name: "Daniel", roleKey: "fullStackEngineer", avatar: Daniel, linkedin: "https://www.linkedin.com/in/daniel-p-c-hansen-8032a7219/" },
    { name: "Jarl", roleKey: "softwareEngineer", avatar: Jarl, linkedin: "https://www.linkedin.com/in/jarl-alexander-g-28a326142/" },
    { name: "Oliver", roleKey: "accountManager", avatar: Oliver, linkedin: "https://www.linkedin.com/in/oliver-mackenzie-1833761b2/" },
  ];

  return (
    <main className="bg-secBg min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-20 px-4">
        <div className="max-w-screen-xl mx-auto text-center">
          <BlurText
            as="div"
            direction="bottom"
            delay={100}
            stepDuration={0.3}
            animationFrom={{ filter: 'blur(4px)', opacity: 0, y: 8 }}
            animationTo={[
              { filter: 'blur(1px)', opacity: 0.5, y: 2 },
              { filter: 'blur(0px)', opacity: 1, y: 0 }
            ]}
            className="justify-center"
          >
            <div className="mb-4 font-medium text-5xl md:text-6xl">
              {t('aboutUs.title')}
            </div>
            <p className="md:text-lg lg:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed mb-10">
              {t('aboutUs.subtitle')}
            </p>
          </BlurText>
        </div>
      </section>

      {showRest ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          {/* Mission & Vision Cards */}
          <section className="pb-16 px-4">
            <div className="max-w-screen-xl mx-auto">
              <div className="grid md:grid-cols-2 gap-10">
                {/* Mission Card */}
                <motion.div
                  className="bg-bg rounded-lg p-6 shadow border border-gray-200 dark:border-gray-700"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: 0.05 }}
                >
                  <div className="w-16 h-16 bg-main/10 rounded-2xl flex items-center justify-center mb-6 " style={{ backgroundColor: `${dark ? 'rgba(128,171,249,0.15)' : 'rgba(22,115,255,0.12)'}` }}>
                    <FaRocket className="text-2xl text-main" />
                  </div>
                  <h2 className="text-2xl md:text-3xl tracking-tight font-extrabold text-text mb-4">
                    {t('aboutUs.mission.title')}
                  </h2>
                  <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    {t('aboutUs.mission.description')}
                  </p>
                </motion.div>

                {/* Story Card */}
                <motion.div
                  className="bg-bg rounded-lg p-6 shadow border border-gray-200 dark:border-gray-700"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                >
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: `${dark ? 'rgba(128,171,249,0.15)' : 'rgba(22,115,255,0.12)'}` }}>
                    <FaBookOpen className="text-2xl" style={{ color: accent }} />
                  </div>
                  <h2 className="text-2xl md:text-3xl tracking-tight font-extrabold text-text mb-4">
                    {t('aboutUs.story.title')}
                  </h2>
                  <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    {t('aboutUs.story.description')}
                  </p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Values Section */}
          <section className="py-16 px-4 bg-bg">
            <div className="max-w-screen-xl mx-auto">
              <div className="text-center mb-12">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: 0 }}
                >
                  <h2 className="text-4xl md:text-5xl tracking-tight font-extrabold text-text mb-4">
                    {t('aboutUs.values.title')}
                  </h2>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.45, delay: 0 }}
                >
                  <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                    {t('aboutUs.values.subtitle')}
                  </p>
                </motion.div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {values.map((value, index) => {
                  const IconComponent = value.icon;
                  return (
                    <motion.div
                      key={value.key}
                      className="group"
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.15 }}
                      transition={{ duration: 0.45, delay: index * 0.08 }}
                    >
                      <div className="bg-secBg rounded-lg p-6 h-full transition-all duration-200 group-hover:shadow-xl border border-gray-200 dark:border-gray-700">
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-transform" style={{ background: dark ? 'linear-gradient(135deg, #6B92D9, #145CD9)' : 'linear-gradient(135deg, #3B82F6, #1D4ED8)' }}>
                          <IconComponent className="text-white text-2xl" />
                        </div>
                        <h3 className="text-lg md:text-xl font-semibold text-text mb-2 text-center">
                          {t(`aboutUs.values.${value.key}.title`)}
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-center text-sm md:text-base">
                          {t(`aboutUs.values.${value.key}.description`)}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Team Section */}
          <section className="py-16 px-4">
            <div className="max-w-screen-xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl tracking-tight font-extrabold text-text mb-4">
                  {t('aboutUs.team.title')}
                </h2>
              </div>

              {/* Add team grid */}
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                {team.map((member, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.45, delay: index * 0.06 }}
                  >
                    <TeamCard
                      name={member.name}
                      role={t(`aboutUs.team.roles.${member.roleKey}`)}
                      avatar={member.avatar}
                      linkedin={member.linkedin}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          <CallToAction reversed={true} />
        </motion.div>
      ) : null}

    </main>
  );
};

const AboutUs = () => {
  const { t } = useTranslation();

  return (
    <NoAuth
      title={t('aboutUs.pageTitle')}
      component={AboutUsContent}
    />
  );
};

export default AboutUs;
